import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { accounts } from '../backend';
import { useApp } from '../store';
import { settleTheAddress, companyInAddress, whichCompany, addressFor } from '../company';
import { MENU_MS, useDismiss } from '../motion';
import { can } from '../permissions';
import { confirmRemoveSamples } from '../sample-note';
import { boardSignal } from '../board-signal';
import type { Project } from '../projects';
import Icon from './Icon';

type Workspace = { id: string; name: string; slug: string; isAdmin: boolean; visiting: boolean; suspended?: boolean };

/**
 * ONE SWITCHER FOR WHAT YOU ARE LOOKING AT AND WHOSE IT IS.
 *
 * This was three things stacked at the top of the sidebar — a workspace chip
 * (WorkspaceSwitcher), the board switcher below it (ProjectSwitcher), and a
 * strip across the very top naming the company (CompanyStrip) — all answering
 * "where am I" before the first place you could actually go. Folded into one
 * (Vinnie, 26 Sep — mock S1): the BOARD leads, the COMPANY is the quiet line
 * beneath it, and the menu holds both lists.
 *
 * THE MENU IS NOW A PROPER BOARD PICKER (Vinnie, 20 Oct — design-lead re-ground
 * mock 05/06). The everyday move — switching board — leads: a search, then your
 * Favourites and Recent boards as rich rows (a health dot + a one-line "where it
 * stands" sub-line), a "Browse all" that opens the full list, a read-only Sample
 * pair set apart, and a green "Start a board" pinned at the top. The rarer move —
 * switching WORKSPACE, which reloads the whole tenant — sits at the FOOT under its
 * own rule. Each row's health is the SAME derived roll-up the Portfolio screen
 * shows (`s.portfolio`), loaded in the background on first open and cached, so the
 * menu opens instantly and the dots fill in a beat later — never a second,
 * differently-computed health.
 *
 * TWO LISTS, TWO KINDS OF MOVE. Switching WORKSPACE reloads — it changes the
 * answer to every question the app has cached (every board, issue, person, live
 * watcher, and the tenant the backend stamps on writes), and a reload is the one
 * thing that cannot be half-done (see go(), carried over verbatim from the old
 * WorkspaceSwitcher, with its #253 address hop). Switching PROJECT is in-app
 * state, so it just sets the project.
 *
 * THE COMPANY LINE CARRIES WHAT THE STRIP CARRIED. It names the company at all
 * times (#218), tinted and qualified "support visit" on a staff visit, and
 * tinted "away" when the company on screen is not your home one — so a glance up
 * still answers "whose data is this".
 */

/** A board is listed apart as a favourite or a recent only until there are few
 *  enough that the whole list fits plainly — below this, the menu shows one flat
 *  alphabetical list instead of the Favourites / Recent / Browse-all split. */
const FLATTEN_AT = 6;
const FAV_KEY = 'flightdeck-fav-boards';
const RECENT_KEY = 'flightdeck-recent-boards';

/** Per-viewer id lists, kept in localStorage. Every read and write is guarded —
 *  a private window, blocked storage or a parse error reads as "none", and the
 *  menu works the same, just without remembered favourites or recents. */
function readIds(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const arr: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
function writeIds(key: string, ids: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    /* storage unavailable — the list just isn't remembered across sessions */
  }
}

export default function Switcher() {
  // Projects — in-app, from the store.
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const setProject = useApp((s) => s.setProject);
  const meta = useApp((s) => s.meta);
  const role = useApp((s) => s.shownRole);
  const mayStart = can(role, 'createProject');
  // Removing the samples archives boards for the whole workspace, so the action
  // is a workspace admin's — the same authority that archives boards (mock 01).
  // Everyone else sees the band exactly as approved, label only.
  const mayManageSamples = can(role, 'manageWorkspace');
  const removeSamples = useApp((s) => s.removeSamples);
  const setStarting = useApp((s) => s.setStartingBoard);

  // The cross-board roll-up — the SAME rows the Portfolio screen shows — for each
  // board's health dot + sub-line. Loaded on demand; the menu kicks off the load
  // in the background on first open and reads what is there, cached.
  const portfolio = useApp((s) => s.portfolio);
  const portfolioIssues = useApp((s) => s.portfolioIssues);
  const portfolioLoading = useApp((s) => s.portfolioLoading);
  const loadPortfolio = useApp((s) => s.loadPortfolio);

  // Workspaces — a reload to switch, fetched the way WorkspaceSwitcher did.
  const [mine, setMine] = useState<Workspace[]>([]);
  const [here, setHere] = useState('');
  const [going, setGoing] = useState('');
  const [problem, setProblem] = useState('');

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [favs, setFavs] = useState<string[]>(() => readIds(FAV_KEY));
  const [recents, setRecents] = useState<string[]>(() => readIds(RECENT_KEY));
  const { leaving, dismiss } = useDismiss(useCallback(() => setOpen(false), []), MENU_MS);
  const wrap = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let stop = false;
    accounts
      .workspaces()
      .then((list) => {
        if (stop) return;
        setMine(list);
        // The address decides which is current, then the token — the same order
        // my_tenant() uses, so the corner and the queries cannot drift (#247).
        const decided = whichCompany(companyInAddress() ?? '', list);
        const id = typeof decided === 'object' ? decided.id : '';
        setHere(id);
        // The address corrects itself once, for somebody who arrived without a
        // company in the path (#218).
        settleTheAddress(list.find((w) => w.id === id)?.slug ?? '');
      })
      // A switcher that cannot list them just shows the board half.
      .catch(() => setMine([]));
    return () => {
      stop = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) dismiss();
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      dismiss();
      // Return focus to the trigger on a KEYBOARD close, so a keyboard user keeps
      // their place rather than being dropped to the top of the page. Not on
      // click-away, where focus has moved somewhere by the user's own intent.
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open, dismiss]);

  // Open resets the search and the Browse-all expansion, and lands focus in the
  // search box so you can type a board name straight away.
  useEffect(() => {
    if (open) {
      setQuery('');
      setShowAll(false);
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  // Fill the health roll-up in the BACKGROUND the first time the menu opens, if a
  // screen hasn't already loaded it. The menu never waits on this — rows render at
  // once and the dots appear when it lands.
  useEffect(() => {
    if (open && portfolio.length === 0 && !portfolioLoading) void loadPortfolio();
  }, [open, portfolio.length, portfolioLoading, loadPortfolio]);

  const currentProject = projects.find((p) => p.id === projectId);
  const name = currentProject?.name ?? meta.name;
  const key = currentProject?.key ?? meta.key;

  const currentWs = mine.find((w) => w.id === here) ?? mine[0];
  const visiting = !!currentWs?.visiting;
  // Home is the oldest company you actually belong to; a company you only visit
  // is never "yours". The list comes back oldest-first.
  const home = mine.find((w) => !w.visiting && !w.suspended);
  const away = !visiting && !!home && !!currentWs && currentWs.id !== home.id;
  const tone = visiting ? 'is-visiting' : away ? 'is-away' : '';
  const memberCount = mine.filter((w) => !w.visiting).length;

  // What the menu can actually do. Switching workspace is only for staff visits
  // or people in more than one; switching project needs more than one board or
  // the right to start one. Nothing to do at all → a bare label, never a control
  // that does nothing when pressed.
  const canSwitchWs = mine.length > 1 || visiting;
  const canSwitchProject = projects.length > 1 || mayStart;
  const inert = !canSwitchWs && !canSwitchProject;

  // The board lists. Archived boards never appear; the read-only sample pair is
  // held apart under its own band. A tenant with no sample seeded simply shows
  // no band, so the picker degrades cleanly.
  const liveBoards = useMemo(() => projects.filter((p) => !p.archived), [projects]);
  const sampleBoards = useMemo(() => liveBoards.filter((p) => p.sample), [liveBoards]);
  const realBoards = useMemo(() => liveBoards.filter((p) => !p.sample), [liveBoards]);
  const byId = useMemo(() => new Map(realBoards.map((p) => [p.id, p])), [realBoards]);
  const alpha = useMemo(() => [...realBoards].sort((a, b) => a.name.localeCompare(b.name)), [realBoards]);
  const rowById = useMemo(() => new Map(portfolio.map((r) => [r.id, r])), [portfolio]);

  const q = query.trim().toLowerCase();
  const searchHits = q
    ? realBoards.filter((p) => p.name.toLowerCase().includes(q) || p.key.toLowerCase().includes(q))
    : [];
  const favBoards = favs.map((id) => byId.get(id)).filter((p): p is Project => !!p);
  const recentBoards = recents
    .map((id) => byId.get(id))
    .filter((p): p is Project => !!p && !favs.includes(p.id) && p.id !== projectId)
    .slice(0, 3);
  const flat = realBoards.length <= FLATTEN_AT;

  const openOf = (id: string): number | null => {
    const list = portfolioIssues.get(id);
    if (!list) return null;
    return list.filter((i) => i.status !== 'done' && i.status !== 'wontdo').length;
  };

  // Persist to localStorage SYNCHRONOUSLY, from the current state, BEFORE the
  // board switch re-renders (and may remount) this component. A write deferred
  // inside a state updater can be dropped when setProject re-renders over the
  // not-yet-committed update — which is why the first switch of a session was not
  // being recorded in Recent. The write is the source of truth; setState just
  // keeps the in-memory copy in step for this render.
  const toggleFav = (id: string) => {
    const next = favs.includes(id) ? favs.filter((x) => x !== id) : [id, ...favs];
    writeIds(FAV_KEY, next);
    setFavs(next);
  };
  const pick = (id: string) => {
    const next = [id, ...recents.filter((x) => x !== id)].slice(0, 8);
    writeIds(RECENT_KEY, next);
    setRecents(next);
    setProject(id);
    dismiss();
  };

  // "Remove these" on the Sample band — the app's own cautionary confirm, then
  // the pair is archived (mock 02). The menu stays open and the band simply
  // falls away; bringing them back lives in Settings → Workspace.
  const removeSamplesNow = async () => {
    if (await confirmRemoveSamples()) await removeSamples();
  };

  const row = (p: Project) => {
    const isCurrent = p.id === projectId;
    const isFav = favs.includes(p.id);
    const sig = boardSignal(p, rowById.get(p.id), openOf(p.id));
    return (
      <button
        key={p.id}
        role="menuitem"
        className={`pm-row ${isCurrent ? 'is-current' : ''}`}
        onClick={() => pick(p.id)}
        aria-label={`${p.name}${isCurrent ? ', the board you are on' : ''} — ${sig.text}`}
      >
        <span className="project-badge">{p.key}</span>
        <span className="pm-id">
          <span className="pm-name">{p.name}</span>
          <span className={`pm-sub ${sig.tone}`}>{sig.text}</span>
        </span>
        <span className="pm-right">
          <span className={`pm-health ${sig.dot}`} aria-hidden="true" />
          {isCurrent ? (
            <span className="pm-chk">
              <Icon name="check" size={16} />
            </span>
          ) : (
            <span
              role="button"
              tabIndex={0}
              className={`pm-star ${isFav ? '' : 'ghost'}`}
              aria-label={isFav ? `Remove ${p.name} from favourites` : `Add ${p.name} to favourites`}
              aria-pressed={isFav}
              onClick={(e) => {
                e.stopPropagation();
                toggleFav(p.id);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFav(p.id);
                }
              }}
            >
              <Icon name={isFav ? 'star-filled' : 'star'} size={17} />
            </span>
          )}
        </span>
      </button>
    );
  };

  // The board code folds into the workspace line ("CSM · FlightDeck") rather than
  // sitting as a left tile — a centred tile floats against a two-line board name
  // (Vinnie, 22 Oct, option D). The code reads in the app's mono ref style.
  const summary = (
    <div className="project-id">
      <div className="project-name">{name}</div>
      {(key || currentWs) && (
        <div className={`project-key ${tone}`}>
          {key && <span className="pk-code">{key}</span>}
          {key && currentWs ? ' · ' : ''}
          {currentWs ? (visiting ? `${currentWs.name} · support visit` : currentWs.name) : ''}
        </div>
      )}
    </div>
  );

  // A VENDOR REACHES EXACTLY ONE BOARD, so the switcher is not a switcher — it is
  // a plain board-identity chip that still answers "which board am I on", with a
  // quiet Vendor-access marker (B-01). No trigger, no menu, no list of other boards
  // or workspaces: a vendor must never be able to load, or even see, boards that are
  // not theirs. The restriction is stated once, calmly, never as an error.
  if (role === 'vendor') {
    return (
      <div className="project-chip project-chip-vendor" aria-label={`${name} — you are a vendor on this board`}>
        {summary}
        <div className="vendor-mark">
          <Icon name="shield" size={13} className="vendor-mark-glyph" />
          Vendor access
        </div>
      </div>
    );
  }

  if (inert) return <div className="project-chip">{summary}</div>;

  const go = async (id: string) => {
    if (id === currentWs?.id) return dismiss();
    setGoing(id);
    setProblem('');
    try {
      await accounts.chooseWorkspace(id);
      // To the OTHER company's address, not a reload where we stand — the path
      // carries the company, so a plain reload lands back in the one just left
      // (#253). The same line the chooser in App.tsx uses.
      const slug = mine.find((w) => w.id === id)?.slug ?? '';
      if (slug) window.location.assign(addressFor(slug));
      else window.location.reload();
    } catch (e) {
      setProblem((e as Error).message || 'That did not work. Try again.');
      setGoing('');
    }
  };

  return (
    <div className="project-wrap" ref={wrap}>
      <button
        ref={triggerRef}
        className="project-chip project-trigger"
        data-reveal="board-picker"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={
          visiting
            ? `Support visit — ${currentWs?.name}'s data. Switch board or workspace.`
            : away
              ? `You are in ${currentWs?.name}, not your home workspace. Switch board or workspace.`
              : 'Switch board or workspace'
        }
      >
        {summary}
        <Icon name="chevron" size={18} className="project-caret" />
      </button>

      {open && (
        <div className={`project-menu ${leaving ? 'is-leaving' : ''}`} role="menu">
          {canSwitchProject && (
            <>
              {/* 1 · Search — focus lands here on open; scoped to BOARDS only, so
                  a text match never triggers a tenant-reloading workspace switch
                  by reflex. The ⌘K hint names the fast-follow shortcut. */}
              <div className="pm-search">
                <span className="sic">
                  <Icon name="search" size={16} />
                </span>
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search boards…"
                  aria-label="Search boards"
                />
                <span className="pm-kbd">⌘K</span>
              </div>

              {/* 2 · Start a board — the primary action, pinned at the top as a
                  green button (Vinnie, 20 Oct) so it is the first thing under the
                  hand and never scrolls off. */}
              {mayStart && (
                <button
                  role="menuitem"
                  className="pm-start"
                  onClick={() => {
                    setStarting(true);
                    dismiss();
                  }}
                >
                  <span className="pm-start-icon">
                    <Icon name="plus" size={15} />
                  </span>
                  Start a board
                </button>
              )}

              {/* 3 · The boards themselves. Searching flattens to matches; a small
                  workspace flattens to one alphabetical list; otherwise Favourites
                  and Recent lead, with Browse-all opening the rest. */}
              {q ? (
                searchHits.length ? (
                  searchHits.map((p) => row(p))
                ) : (
                  <div className="pm-none">No board matches “{query.trim()}”</div>
                )
              ) : realBoards.length === 0 ? (
                mayStart ? (
                  <div className="pm-empty">
                    <h4>No boards yet</h4>
                    <p>A board is where a project or a service is tracked. Start your first one.</p>
                  </div>
                ) : (
                  <div className="project-menu-label">No boards yet</div>
                )
              ) : flat ? (
                alpha.map((p) => row(p))
              ) : (
                <>
                  {favBoards.length > 0 && (
                    <>
                      <div className="pm-band">
                        <span className="bic">
                          <Icon name="star" size={14} />
                        </span>
                        Favourites
                      </div>
                      {favBoards.map((p) => row(p))}
                    </>
                  )}
                  {recentBoards.length > 0 && (
                    <>
                      <div className="pm-band">Recent</div>
                      {recentBoards.map((p) => row(p))}
                    </>
                  )}
                  {/* With nothing pinned or recent yet, the full list IS the menu —
                      shown inline so a newcomer sees their boards, not a lone
                      Browse-all row. It flows into the menu's own height (no inner
                      cap), so a short list never scrolls at rest (R1). Once there
                      are favourites or recents, the full list hides behind
                      Browse-all, which opens it in a capped, scrollable panel by
                      choice — the one place a long list may scroll. */}
                  {favBoards.length === 0 && recentBoards.length === 0 ? (
                    alpha.map((p) => row(p))
                  ) : showAll ? (
                    <div className="pm-all">{alpha.map((p) => row(p))}</div>
                  ) : (
                    <button className="pm-allboards" onClick={() => setShowAll(true)}>
                      Browse all {realBoards.length} boards
                      <Icon name="chevron" size={16} />
                    </button>
                  )}
                </>
              )}

              {/* 5 · Sample · read only — the worked examples, set apart under a
                  teal rule. Shown only once the pair has been seeded. */}
              {sampleBoards.length > 0 && (
                <>
                  <div className="pm-rule" />
                  <div className="pm-band teal">
                    <span className="pm-band-lbl">Sample · read only</span>
                    {/* Admin-only, and quiet — same weight as the band label, it
                        goes brand-teal on hover; lower-case, so it is not a new
                        uppercase heading (mock 01). Archives the pair on confirm. */}
                    {mayManageSamples && (
                      <button className="pm-band-remove" onClick={() => void removeSamplesNow()}>
                        Remove these
                      </button>
                    )}
                  </div>
                  {sampleBoards.map((p) => (
                    <button key={p.id} role="menuitem" className="pm-row" onClick={() => pick(p.id)}>
                      <span className="project-badge sample">{p.key}</span>
                      <span className="pm-id">
                        <span className="pm-name">{p.name}</span>
                        <span className="pm-sub">
                          {p.deliveryMode === 'continuous'
                            ? 'Example Co. · an ongoing service board'
                            : 'Example Co. · a finished delivery project'}
                        </span>
                      </span>
                    </button>
                  ))}
                </>
              )}
            </>
          )}

          {/* 7 · Workspaces — the heavier, rarer switch (it reloads the whole
              tenant), at the foot under its own rule. Most people are in one and
              never see this; the company is still always named on the trigger. */}
          {canSwitchWs && (
            <>
              {canSwitchProject && <div className="pm-rule" />}
              {/* Counts memberships only — somewhere you are visiting is not
                  somewhere you are in. */}
              <div className="project-menu-label">
                You are in {memberCount} workspace{memberCount === 1 ? '' : 's'}
              </div>
              {mine.map((w) => (
                <button
                  key={w.id}
                  role="menuitem"
                  aria-label={
                    w.id === currentWs?.id
                      ? `${w.name}, where you are now${w.visiting ? ', a support visit' : ''}`
                      : `${w.visiting ? 'Visit' : 'Switch to'} ${w.name}`
                  }
                  className={`workspace-option ${w.id === currentWs?.id ? 'is-current' : ''}`}
                  disabled={!!going}
                  onClick={() => go(w.id)}
                >
                  <span className="workspace-mark">{(w.name[0] ?? '?').toUpperCase()}</span>
                  <span className="workspace-option-name">{w.name}</span>
                  {w.isAdmin && <span className="workspace-admin">Admin</span>}
                  {w.visiting && (
                    <span className="workspace-staff" title="Support access, and it expires">
                      Staff
                    </span>
                  )}
                  {w.id === currentWs?.id && (
                    <span className="workspace-here">
                      Here
                      <Icon name="check" size={13} />
                    </span>
                  )}
                  {going === w.id && <span className="workspace-going">Moving…</span>}
                </button>
              ))}
              {problem && <div className="workspace-problem">{problem}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
